import { http } from '@app/lib/api'
import { appVersion, currentPageForReport, describeError } from '@app/lib/errors'
import type { Me } from '@shared/contracts/identity'
import { QueryClientContext } from '@tanstack/react-query'
import { useContext, useRef, useState } from 'react'
import { Dialog, Field } from './index'

/**
 * "Report a problem": a link-style button that opens a short form. The page address (private
 * links removed), the error, the request id, the browser and the app version go along with it,
 * so the person only has to say what happened, if they want to.
 */
export function ReportProblemButton({
  error,
  label = 'Report a problem',
  className = 'link-btn',
}: {
  error?: unknown
  label?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const close = () => {
    setOpen(false)
    // The dialog is removed rather than closed, so put focus back where it was.
    window.setTimeout(() => trigger.current?.focus(), 0)
  }
  return (
    <>
      <button ref={trigger} type="button" className={className} onClick={() => setOpen(true)}>
        {label}
      </button>
      {open ? (
        <Dialog open onClose={close} title="Report a problem">
          <ReportProblemForm error={error} onDone={close} />
        </Dialog>
      ) : null}
    </>
  )
}

function ReportProblemForm({ error, onDone }: { error?: unknown; onDone: () => void }) {
  // Read from the cache rather than useAuth, so this also works outside the auth provider.
  const qc = useContext(QueryClientContext)
  const signedInEmail = qc?.getQueryData<Me>(['me'])?.user?.email ?? ''
  const honeypot = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')
  const [failure, setFailure] = useState<string | null>(null)

  async function submit(form: HTMLFormElement) {
    const data = new FormData(form)
    const info = error === undefined ? null : describeError(error)
    setState('sending')
    setFailure(null)
    try {
      await http.post('/api/v1/public/problem-reports', {
        message: String(data.get('message') ?? ''),
        email: String(data.get('email') ?? ''),
        pageUrl: currentPageForReport(),
        errorText: info?.detail.slice(0, 4000),
        requestId: info?.requestId ?? undefined,
        appVersion: appVersion(),
        homepage: honeypot.current?.value || undefined,
      })
      setState('sent')
    } catch (e) {
      setFailure(describeError(e).message)
      setState('failed')
    }
  }

  if (state === 'sent')
    return (
      <div className="stack--sm">
        <p role="status">Thank you — your report was sent. We’ll look into it.</p>
        <div className="cluster">
          <button type="button" className="btn" onClick={onDone}>
            Close
          </button>
        </div>
      </div>
    )

  return (
    <form
      className="stack--sm"
      onSubmit={(e) => {
        e.preventDefault()
        void submit(e.currentTarget)
      }}
    >
      <Field label="What happened?" help="Optional. What were you trying to do?">
        {(id, describedBy) => (
          <textarea
            id={id}
            name="message"
            className="input"
            rows={4}
            maxLength={4000}
            aria-describedby={describedBy}
          />
        )}
      </Field>
      <Field label="Email (optional, so we can follow up)">
        {(id) => (
          <input
            id={id}
            name="email"
            type="email"
            className="input"
            autoComplete="email"
            defaultValue={signedInEmail}
            maxLength={254}
          />
        )}
      </Field>
      {/* Honeypot: hidden from people and assistive tech; bots fill it in. */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label>
          Homepage
          <input ref={honeypot} name="homepage" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <p className="tiny muted mb-0">
        We’ll include this page’s address (with any private links removed), the error, your browser
        and the app version.
      </p>
      {failure ? (
        <div className="alert alert--error" role="alert">
          Your report wasn’t sent. {failure}
        </div>
      ) : null}
      <div className="cluster">
        <button type="submit" className="btn" disabled={state === 'sending'}>
          {state === 'sending' ? 'Sending…' : 'Send'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
